import { useState } from "react";
import { TableSegment } from "../utils/segmentationTableUtils";
import { DriverTableSegment } from "../utils/driverTableUtils";
import { HeadroomTableSegment } from "../utils/headroomTableUtils";

interface UseTableSort {
  sortColumn: string;
  sortDirection: "asc" | "desc";
  handleSort: (column: string) => void;
}

/**
 * A custom React hook for managing table sorting functionality.
 *
 * @param segments - An array of TableSegment or DriverTableSegment objects representing the table data.
 * @param hasOneDataAndOneManualSegment - A boolean indicating if the table has exactly one data segment and one manual segment.
 * @returns An object containing the current sort column, sort direction, and a function to handle sorting.
 */
export const useTableSort = (
  segments: TableSegment[] | DriverTableSegment[] | HeadroomTableSegment[],
  hasOneDataAndOneManualSegment: boolean
): UseTableSort => {
  /**
   * Determines the initial column to sort based on segment conditions.
   * - If there's only one manual segment, sorts by "segmentValue".
   * - If there's only one data segment, sorts by "segmentValue".
   * - If there's one data and one manual segment, sorts by "range".
   * - If there are two segments of the same method and both are data, sorts by "segmentValue1".
   * - Otherwise, sorts by "range1".
   */
  const isManualOnly =
    segments.length === 1 &&
    (segments as (TableSegment | DriverTableSegment)[]).every((seg) => seg.method === "MANUAL");
  const isDataOnly =
    segments.length === 1 && (segments as (TableSegment | DriverTableSegment)[]).every((seg) => seg.method === "DATA");
  const isTwoSameMethod =
    segments.length === 2 &&
    (segments as (TableSegment | DriverTableSegment)[]).every((seg) => seg.method === segments[0].method);
  const initialColumn = isManualOnly
    ? "segmentValue"
    : isDataOnly
    ? "segmentValue"
    : hasOneDataAndOneManualSegment
    ? "range"
    : isTwoSameMethod && segments[0].method === "DATA"
    ? "segmentValue1"
    : "range1";

  const [sortColumn, setSortColumn] = useState<string>(initialColumn);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  /**
   * Handles sorting logic when a column is clicked.
   * - If the clicked column is already the sort column, toggles the sort direction.
   * - If a different column is clicked, sets it as the new sort column and defaults to ascending order.
   *
   * @param column - The column to sort by.
   */
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  return { sortColumn, sortDirection, handleSort };
};
